import { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getAuthClient } from '@/api/client';
import type { UserSiteMembership } from '@/api/types';
import { useAuthStore } from '@/stores/useAuthStore';

const STATUS_LABEL: Record<string, string> = {
  pending: '승인 대기',
  approved: '승인됨',
  rejected: '거절',
};

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [memberships, setMemberships] = useState<UserSiteMembership[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAuthClient()
      .listMySiteMemberships()
      .then((items) => {
        if (!cancelled) setMemberships(items);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  return (
    <AppShell>
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <h1 className="text-2xl font-semibold">안녕하세요 {user.name}님</h1>

        <Card>
          <CardHeader>
            <CardTitle>현재 소속</CardTitle>
          </CardHeader>
          <CardContent>
            {user.orgs.length === 0 ? (
              <div className="text-muted-foreground">
                무소속 — 조직 찾기에서 가입 요청하세요.
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {user.orgs.map((o) => (
                  <li key={o.id} className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{o.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {o.role === 'org_admin' ? '관리자' : '일반 멤버'}
                        {o.is_primary && ' · 주 소속'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="size-4" />
              내 사이트
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-muted-foreground">불러오는 중...</div>
            ) : memberships.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                아직 소속 사이트가 없습니다.
              </div>
            ) : (
              <ul className="flex flex-col divide-y">
                {memberships.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div>
                      <div className="font-medium">
                        {m.site_name ?? `사이트 ${m.site_id.slice(0, 8)}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        요청일: {new Date(m.requested_at).toLocaleDateString('ko-KR')}
                      </div>
                    </div>
                    <Badge
                      variant={
                        m.status === 'approved'
                          ? 'default'
                          : m.status === 'pending'
                            ? 'secondary'
                            : 'destructive'
                      }
                    >
                      {STATUS_LABEL[m.status] ?? m.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>최근 로그인</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {user.last_login_at
              ? new Date(user.last_login_at).toLocaleString('ko-KR')
              : '기록 없음'}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
